import { type ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { areReferenceCropYearsSaved, isApiScenarioId } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { withEmptyYearsPruned } from "pages/app/scenario-analysis/utils/scenarioBuilderState";
import { areScenariosReadyForRecalculation, referenceYearsAreComplete } from "pages/app/scenario-analysis/utils/scenarioValidation";

/** What the page does once a project has hydrated. Every action except "wait" is taken once per project load. */
type PostHydrationAction =
  /** Not decidable yet; asked again when the inputs change. */
  | "wait"
  /** Field-import landing with a complete rotation: prune, then run the landing recalculation. */
  | "landingRecalc"
  /** Field-import landing that cannot run yet: mark dirty and show the first incomplete scenario. */
  | "landingIncomplete"
  /** A rotation was just added to an existing project: let the user review it before running. */
  | "reviewAddedRotation"
  /** The reference itself is not ready: do nothing, so an untouched project is not greeted with errors. */
  | "stayQuiet"
  /** Everything is complete and saved: prune, then run in the background. */
  | "recalc"
  /** Some scenario is incomplete: mark dirty, flag the errors and show the first incomplete scenario. */
  | "showIncomplete";

export type PostHydrationInput = {
  hydrationApplied: boolean;
  hasProject: boolean;
  /** Already decided for this project load. */
  isDecided: boolean;
  scenarios: ScenarioData[];
  /** Autosave ledger of saved crop years per scenario id. */
  savedCropYears: Map<string, string>;
  isSaving: boolean;
  isFieldListFetching: boolean;
  isFieldReselectNeeded: boolean;
  /** A field was just reselected; its own deferred recalculation runs instead. */
  isFieldReselectPending: boolean;
  /** Crop years were persisted by a field import before navigating here. */
  isFieldImportLanding: boolean;
  /** The imported rotation was complete before navigation. */
  wasImportReadyForRecalc: boolean;
  /** The entry dialog has not placed the imported years yet. */
  isAwaitingProjectChoice: boolean;
  isAddToExistingFlow: boolean;
};

export const decidePostHydrationAction = ({
  hydrationApplied,
  hasProject,
  isDecided,
  scenarios,
  savedCropYears,
  isSaving,
  isFieldListFetching,
  isFieldReselectNeeded,
  isFieldReselectPending,
  isFieldImportLanding,
  wasImportReadyForRecalc,
  isAwaitingProjectChoice,
  isAddToExistingFlow,
}: PostHydrationInput): PostHydrationAction => {
  if (!hydrationApplied || !hasProject || isDecided) return "wait";
  if (!scenarios.some((scenario) => isApiScenarioId(scenario.id))) return "wait";
  if (isSaving) return "wait";
  // A missing field is the reselection modal's job, and a settling reselect belongs to its deferred effect.
  if (isFieldListFetching || isFieldReselectNeeded || isFieldReselectPending) return "wait";

  // A read-only prune to judge readiness; the saving prune runs only once the page decides to recalculate.
  const preview = withEmptyYearsPruned(scenarios);
  // Completeness uses the pruned preview, but "is it saved" compares the real years: the ledger holds the full array.
  const cropYearsReady = referenceYearsAreComplete(preview) && areReferenceCropYearsSaved(scenarios, savedCropYears);
  const allScenariosReady = areScenariosReadyForRecalculation(preview);

  if (isFieldImportLanding) {
    // The import flag cannot vouch for the reference or other scenarios, so the hydrated state must be complete too.
    const allProjectDataReady = referenceYearsAreComplete(preview) && allScenariosReady;
    const isImportReady = wasImportReadyForRecalc || (cropYearsReady && allScenariosReady);
    return isImportReady && allProjectDataReady ? "landingRecalc" : "landingIncomplete";
  }

  // The user's choice in the entry dialog (create new / add to existing / dismiss) is decided once it lands.
  if (isAwaitingProjectChoice) return "wait";
  if (isAddToExistingFlow) return "reviewAddedRotation";
  if (!cropYearsReady) return "stayQuiet";
  // The import flag only reflects the pre-navigation rotation, so the loaded scenarios decide on their own.
  return allScenariosReady ? "recalc" : "showIncomplete";
};
